import { FC, Fragment, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as S from './styles';
import {
  DOT_RADIUS,
  STROKE_WIDTH,
  buildPitchSvgProps,
  describePitchVariants,
  hasRenderablePitchAccent,
} from './utils';

const msgs = defineMessages({
  or: { id: 'daily_activities_interstitial_or' },
});

export interface PitchAccentProps {
  accents?: number[] | null;
  reading?: string | null;
}

const PitchDiagram: FC<{ pitchNum: number; reading: string }> = ({ pitchNum, reading }) => {
  const { viewBox, color, points, morae, moraLabelY } = buildPitchSvgProps(pitchNum, reading, true);

  return (
    <S.Diagram>
      <S.DiagramSvg viewBox={viewBox} fontSize={1} aria-hidden="true">
        {morae.map((moraChar, mi) => (
          <S.MoraLabel
            key={`mora-${moraChar}-${points[mi]?.x ?? mi}`}
            x={points[mi]?.x ?? mi}
            y={moraLabelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={0.8}
            fill="#595959"
          >
            {moraChar}
          </S.MoraLabel>
        ))}
        <polyline
          fill="none"
          stroke={color}
          strokeWidth={STROKE_WIDTH}
          points={points.map(p => `${p.x},${p.y}`).join(' ')}
        />
        {points.map(p => {
          const isParticle = p.x === points[points.length - 1]?.x;
          return (
            <circle
              key={`dot-${p.x}-${p.y}-${isParticle ? 'particle' : 'mora'}`}
              cx={p.x}
              cy={p.y}
              r={DOT_RADIUS}
              fill={isParticle ? '#eeeeee' : color}
              stroke={isParticle ? 'black' : color}
              strokeWidth={isParticle ? STROKE_WIDTH / 2 : 0}
            />
          );
        })}
      </S.DiagramSvg>
    </S.Diagram>
  );
};

/**
 * Tokyo pitch-accent diagrams with furigana (mora labels) below each
 * mora, plus pattern labels. Multiple accents are whole-word alternatives.
 */
export const PitchAccent: FC<PitchAccentProps> = ({ accents, reading = '' }) => {
  const intl = useIntl();
  const readingValue = reading ?? '';
  const variants = useMemo(
    () => describePitchVariants(accents, readingValue),
    [accents, readingValue],
  );

  if (!hasRenderablePitchAccent(accents, readingValue)) {
    return null;
  }

  const multi = variants.length > 1;
  const title = multi
    ? `Pitch accent alternatives: ${variants.map(v => `${v.num} ${v.name}`).join(', ')}`
    : `Pitch accent: ${variants[0].num} · ${variants[0].name}`;

  return (
    <S.Variants $multi={multi} title={title} data-testid="pitch-accent">
      {variants.map((variant, index) => (
        <Fragment key={`${variant.num}-${variant.name}`}>
          {index > 0 && <S.VariantOr>{intl.formatMessage(msgs.or)}</S.VariantOr>}
          <S.VariantItem data-testid="pitch-accent-variant">
            <PitchDiagram pitchNum={variant.num} reading={readingValue} />
          </S.VariantItem>
        </Fragment>
      ))}
    </S.Variants>
  );
};
